import type { SalaryDefinition } from './salaryDefinition';
import { SALARY_SCHEMA_VERSION } from './salaryDefinition';

/**
 * Somewhere to start, because a blank pricing model is not something anybody can judge.
 *
 * Same reasoning as the points designer's SHRPO ladder (§9.7): an operator meeting this screen has
 * no idea what a sensible band table looks like, and the reference model is one whose every figure
 * this codebase has checked against real data. Every number in it is editable the moment it lands.
 */

/**
 * §4.2's reference model, figure for figure.
 *
 * The 60/25/15 split is not taste. Measured over the reference series, prior cash *count*
 * correlates 0.210 with later earnings where prior *earnings* correlate 0.013 — so a model
 * weighted toward money prices last season's luck. §4.3 has the derivation.
 */
export function referenceSalaryModel(name: string): SalaryDefinition {
  return {
    schemaVersion: SALARY_SCHEMA_VERSION,
    name,
    description: 'Priced mostly on how often somebody cashes, with points and money behind it.',
    lookback: { mode: 'priorLeaderboards', leaderboardIds: [], seriesCount: 1 },
    inputs: [
      { id: 'cashFreq', source: 'cashCount', weight: 0.6 },
      { id: 'boardPoints', source: 'leaderboardPoints', weight: 0.25 },
      { id: 'earnings', source: 'totalEarnings', weight: 0.15 },
    ],
    mapping: {
      type: 'percentileBands',
      bands: [
        { fromPercentile: 0, toPercentile: 50, salary: 3 },
        { fromPercentile: 50, toPercentile: 75, salary: 8 },
        { fromPercentile: 75, toPercentile: 90, salary: 15 },
        { fromPercentile: 90, toPercentile: 98, salary: 28 },
        { fromPercentile: 98, toPercentile: 100, salary: 45 },
      ],
    },
    // §4.5: at every prior casher (941) 45% of entrants finish with nothing; at two cashes (127)
    // that is 16%. Two is the setting that produced the target-sized pool on the reference data.
    qualification: { minCashes: 2, minEvents: null, maxPoolSize: 150 },
    unpriced: { salary: 3, flagAsUnproven: true },
    rounding: { mode: 'nearest', increment: 1 },
  };
}

/**
 * The plainest thing that still prices somebody: one input, three bands, a floor.
 *
 * Deliberately not empty. An empty `inputs` array and an empty band list are both refused by the
 * schema, so "start from scratch" has to start from *something* — and a model with one signal and
 * three prices is the smallest thing an operator can read and then argue with.
 */
export function blankSalaryModel(name: string): SalaryDefinition {
  return {
    schemaVersion: SALARY_SCHEMA_VERSION,
    name,
    description: null,
    lookback: { mode: 'priorLeaderboards', leaderboardIds: [], seriesCount: 1 },
    inputs: [{ id: 'cashFreq', source: 'cashCount', weight: 1 }],
    mapping: {
      type: 'percentileBands',
      bands: [
        { fromPercentile: 0, toPercentile: 70, salary: 3 },
        { fromPercentile: 70, toPercentile: 95, salary: 12 },
        { fromPercentile: 95, toPercentile: 100, salary: 30 },
      ],
    },
    qualification: { minCashes: 2, minEvents: null, maxPoolSize: 150 },
    unpriced: { salary: 3, flagAsUnproven: true },
    rounding: { mode: 'nearest', increment: 1 },
  };
}

/** A new band inserted at the end, before the author fixes its edges. */
export function blankBand() {
  return { fromPercentile: null, toPercentile: null, salary: null };
}
